import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface IrisBlurParams {
  blur: number;
  irisShape: number;
  irisRoundness: number;
  irisCenterX: number;
  irisCenterY: number;
  irisSizeX: number;
  irisSizeY: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const IrisBlur: Filter<IrisBlurParams> = {
  id: "iris-blur",
  displayName: "Iris Blur",
  defaultParams: { blur: 15, irisShape: 6, irisRoundness: 50, irisCenterX: 50, irisCenterY: 50, irisSizeX: 30, irisSizeY: 30 },

  apply(input: PixelBuffer, params: IrisBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const cx = (params.irisCenterX / 100) * w;
    const cy = (params.irisCenterY / 100) * h;
    const rx = (params.irisSizeX / 100) * w / 2;
    const ry = (params.irisSizeY / 100) * h / 2;
    const maxBlur = params.blur;
    const roundness = params.irisRoundness / 100;
    const blurRadius = Math.round(maxBlur);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - cx, dy = y - cy;
        const normalizedDist = Math.sqrt((dx / (rx || 1)) ** 2 + (dy / (ry || 1)) ** 2);
        const blurAmount = Math.max(0, (normalizedDist - 1) * maxBlur * (1 + roundness));

        if (blurAmount < 0.5) {
          const o = (y * w + x) * 4;
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
          continue;
        }

        const r = Math.round(blurAmount);
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

        for (let dy2 = -r; dy2 <= r; dy2++) {
          for (let dx2 = -r; dx2 <= r; dx2++) {
            const sx = Math.max(0, Math.min(w - 1, x + dx2));
            const sy = Math.max(0, Math.min(h - 1, y + dy2));
            const si = (sy * w + sx) * 4;
            sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
            count++;
          }
        }

        const o = (y * w + x) * 4;
        if (count > 0) {
          dst[o] = clamp8(Math.round(sumR / count));
          dst[o + 1] = clamp8(Math.round(sumG / count));
          dst[o + 2] = clamp8(Math.round(sumB / count));
          dst[o + 3] = clamp8(Math.round(sumA / count));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
